import { alpha, createTheme } from '@mui/material/styles';
import type { EmbedTheme } from '@responsedock/types';
import type { ThemeMode } from './store/theme';
import { isInFrame } from './lib/embedResize';
import { FRAMED_VIEWPORT_STYLES } from './lib/frameViewport';

/**
 * Inside a frame, a modal's scroll lock has nothing to lock (an auto-height frame
 * never scrolls) but its scrollbar compensation pads <body>, which reflows the
 * content and changes the height the frame reports to its host.
 */
export function framedModalDefaults(framed: boolean = isInFrame()) {
  if (!framed) return {};
  return {
    MuiModal: { defaultProps: { disableScrollLock: true } },
    MuiPopover: { defaultProps: { disableScrollLock: true } },
  } as const;
}

const FONT_STACK = '"Space Grotesk", "Segoe UI Variable", "Segoe UI", sans-serif';

function getPalette(mode: ThemeMode) {
  if (mode === 'light') {
    return {
      primary: {
        main: '#6366F1',
        light: '#A5B4FC',
        dark: '#4F46E5',
        contrastText: '#F8FAFC',
      },
      secondary: {
        main: '#8B5CF6',
        light: '#C4B5FD',
        dark: '#7C3AED',
        contrastText: '#F8FAFC',
      },
      background: {
        default: '#F5F3EE',
        paper: '#FFFDF9',
      },
      text: {
        primary: '#102018',
        secondary: '#5B6670',
      },
      divider: '#E5DED1',
      error: { main: '#D64545' },
      warning: { main: '#D97706' },
      info: { main: '#2563EB' },
      success: { main: '#0F9D58' },
    } as const;
  }

  return {
    primary: {
      main: '#818CF8',
      light: '#A5B4FC',
      dark: '#6366F1',
      contrastText: '#071118',
    },
    secondary: {
      main: '#A78BFA',
      light: '#C4B5FD',
      dark: '#8B5CF6',
      contrastText: '#071118',
    },
    background: {
      default: '#060C14',
      paper: '#0F1826',
    },
    text: {
      primary: '#E8F2FF',
      secondary: '#A3B4C7',
    },
    divider: '#223247',
    error: { main: '#F87171' },
    warning: { main: '#FBBF24' },
    success: { main: '#34D399' },
    info: { main: '#60A5FA' },
  } as const;
}

export function createAppTheme(mode: ThemeMode) {
  const palette = getPalette(mode);
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      ...palette,
    },
    typography: {
      fontFamily: FONT_STACK,
      fontSize: 13,
      h1: { fontWeight: 700, fontSize: '1.75rem', letterSpacing: '-0.04em' },
      h2: { fontWeight: 700, fontSize: '1.45rem', letterSpacing: '-0.03em' },
      h3: { fontWeight: 650, fontSize: '1.2rem', letterSpacing: '-0.02em' },
      h4: { fontWeight: 650, fontSize: '1.05rem', letterSpacing: '-0.02em' },
      h5: { fontWeight: 650, fontSize: '0.95rem' },
      h6: { fontWeight: 650, fontSize: '0.875rem' },
      body1: { fontSize: '0.875rem' },
      body2: { fontSize: '0.8125rem' },
      caption: { fontSize: '0.75rem' },
      overline: { fontSize: '0.6875rem', letterSpacing: '0.08em' },
      button: { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', letterSpacing: '0.01em' },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundImage: isDark
              ? 'linear-gradient(180deg, #060C14 0%, #08101A 100%)'
              : 'linear-gradient(160deg, #fafafe 0%, #f5f3ee 50%, #f8f6f1 100%)',
            backgroundAttachment: 'fixed',
          },
          'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, textarea:-webkit-autofill, textarea:-webkit-autofill:hover, textarea:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 1000px ${palette.background.paper} inset`,
            WebkitTextFillColor: palette.text.primary,
            caretColor: palette.text.primary,
            transition: 'background-color 9999s ease-out 0s',
          },
          ...FRAMED_VIEWPORT_STYLES,
        },
      },
      ...framedModalDefaults(),
      MuiButton: {
        defaultProps: { disableElevation: true, size: 'small' },
        styleOverrides: {
          root: {
            borderRadius: 7, paddingTop: 4, paddingBottom: 4, paddingLeft: 12, paddingRight: 12, minHeight: 30, fontSize: '0.8125rem', fontWeight: 600, letterSpacing: '0.01em', transition: 'all 0.15s ease',
            // MUI 9 has no containedPrimary class key; match the same buttons by props.
            variants: [{
              props: { variant: 'contained', color: 'primary' },
              style: {
                '&:hover': {
                  boxShadow: isDark ? '0 0 12px rgba(129, 140, 248, 0.25)' : '0 4px 16px rgba(99, 102, 241, 0.2)',
                  transform: 'translateY(-1px)',
                },
                '&:focus-visible': { boxShadow: isDark ? '0 0 12px rgba(129, 140, 248, 0.25)' : '0 4px 16px rgba(99, 102, 241, 0.2)' },
              },
            }],
          },
          sizeLarge: { paddingTop: 7, paddingBottom: 7, paddingLeft: 18, paddingRight: 18, minHeight: 38, fontSize: '0.875rem', borderRadius: 9 },
          sizeSmall: { paddingTop: 2, paddingBottom: 2, paddingLeft: 9, paddingRight: 9, minHeight: 24, fontSize: '0.75rem', borderRadius: 5 },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${palette.divider}`,
            borderRadius: 12,
            backgroundImage: isDark
              ? 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%)'
              : 'none',
            backdropFilter: isDark ? 'blur(4px)' : 'none',
            padding: '12px 0',
            transition: 'all 0.2s ease',
            '&:hover': isDark ? { boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)' } : undefined,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: { padding: '12px 16px', '&:last-child': { paddingBottom: 12 } },
        },
      },
      MuiChip: {
        styleOverrides: { 
          root: { borderRadius: 6, fontSize: '0.75rem', height: 22, fontWeight: 500, transition: 'all 0.15s ease' }
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small', variant: 'outlined' },
      },
      MuiInputBase: {
        styleOverrides: {
          root: { fontSize: '0.875rem', lineHeight: 1.4 },
          input: {
            boxSizing: 'content-box',
            height: '1.25rem',
            lineHeight: '1.25rem',
            '&::placeholder': {
              color: palette.text.secondary,
              opacity: 0.72,
            },
            // MUI 9 has no inputSizeSmall/inputMultiline class keys; multiline comes last so it wins, as before.
            variants: [
              { props: { size: 'small' }, style: { height: '1.2rem', lineHeight: '1.2rem' } },
              { props: { multiline: true }, style: { height: 'auto', lineHeight: 1.5, padding: 0 } },
            ],
          },
          multiline: {
            alignItems: 'flex-start',
          },
          sizeSmall: { fontSize: '0.8125rem' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 7,
            alignItems: 'center',
            minHeight: 38,
            backgroundColor: isDark ? 'rgba(10, 20, 32, 0.75)' : undefined,
            '&.MuiInputBase-sizeSmall': {
              minHeight: 34,
            },
            '&.MuiInputBase-multiline': {
              alignItems: 'flex-start',
              paddingTop: 8,
              paddingBottom: 8,
            },
            '&.MuiInputBase-sizeSmall.MuiInputBase-multiline': {
              paddingTop: 7,
              paddingBottom: 7,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: palette.primary.main, borderWidth: 1.5 },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.primary.main,
              borderWidth: 1.5,
              boxShadow: isDark ? '0 0 0 3px rgba(129, 140, 248, 0.16)' : '0 0 0 3px rgba(99, 102, 241, 0.16)',
            },
          },
          input: {
            padding: '8px 12px',
            lineHeight: '1.25rem',
            // MUI 9 has no inputSizeSmall/inputMultiline class keys; multiline comes last so it wins, as before.
            variants: [
              { props: { size: 'small' }, style: { padding: '7px 10px', lineHeight: '1.2rem' } },
              { props: { multiline: true }, style: { padding: 0, lineHeight: 1.5 } },
            ],
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: '0.875rem' },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { 
            borderRight: `1px solid ${palette.divider}`,
            backgroundImage: isDark ? 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%)' : 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            overflow: 'hidden',
            backgroundImage: 'none',
            border: `1px solid ${palette.divider}`,
            boxShadow: isDark ? '0 20px 60px rgba(0, 0, 0, 0.3)' : '0 20px 60px rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontSize: '0.9375rem', fontWeight: 650, padding: '14px 18px' },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { paddingTop: '12px !important', padding: '12px 18px', fontSize: '0.875rem' },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: '10px 18px' },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            boxShadow: isDark ? '0 8px 28px rgba(0, 0, 0, 0.36)' : '0 4px 12px rgba(0, 0, 0, 0.06)',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: { minHeight: '48px !important', paddingLeft: '16px !important', paddingRight: '16px !important' },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: { paddingTop: 5, paddingBottom: 5, borderRadius: 7 },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: { minWidth: 30, '& svg': { fontSize: '1rem' } },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: { fontSize: '0.8125rem' },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: palette.primary.main, height: 2, borderRadius: 8 },
          root: { minHeight: 38 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 38, padding: '4px 12px', borderRadius: 6, '&.Mui-selected': { color: palette.primary.main } },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? 'rgba(15, 23, 42, 0.9)' : 'rgba(31, 41, 55, 0.9)',
            border: `1px solid ${palette.divider}`,
            fontSize: '0.7rem',
            borderRadius: 5,
            backdropFilter: 'blur(4px)',
            padding: '4px 8px',
          },
          arrow: { color: isDark ? 'rgba(15, 23, 42, 0.9)' : 'rgba(31, 41, 55, 0.9)' },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            minHeight: '1.2rem',
            paddingTop: 7,
            paddingBottom: 7,
            lineHeight: '1.2rem',
          },
          icon: { color: palette.text.secondary },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: { fontSize: '0.8125rem', paddingTop: 5, paddingBottom: 5, minHeight: 'auto' },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: { '& .MuiTableCell-head': { backgroundColor: palette.background.paper, color: palette.text.secondary, fontWeight: 650, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase' } },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: palette.divider, fontSize: '0.8125rem', padding: '8px 14px' },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: { fontSize: '0.75rem' },
        },
      },
    },
  });
}

function numericRadius(value: EmbedTheme['borderRadius']): number {
  if (typeof value === 'number') return value;
  const parsed = Number.parseFloat(value ?? '');
  return Number.isFinite(parsed) ? parsed : 8;
}

/** True for colours dark enough that light text belongs on them (#rgb or #rrggbb, alpha ignored). */
export function isDarkEmbedColor(color: string | undefined): boolean {
  const hex = color?.trim().replace(/^#/, '') ?? '';
  const full = hex.length === 3 || hex.length === 4
    ? hex.slice(0, 3).split('').map((digit) => digit + digit).join('')
    : hex.slice(0, 6);
  if (!/^[\da-f]{6}$/i.test(full)) return false;
  const linear = (offset: number) => {
    const channel = Number.parseInt(full.slice(offset, offset + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(0) + 0.7152 * linear(2) + 0.0722 * linear(4) < 0.18;
}

export function createEmbeddedAppTheme(theme: EmbedTheme) {
  // A dark host must get a dark base: MUI derives disabled text, inactive toggle
  // buttons and switch tracks from the palette mode, and the light defaults
  // turn those into dark text on a dark surface.
  const mode: ThemeMode = isDarkEmbedColor(theme.backgroundColor ?? theme.surfaceColor) ? 'dark' : 'light';
  const base = createAppTheme(mode);
  const primaryColor = theme.primaryColor ?? base.palette.primary.main;
  const backgroundColor = theme.backgroundColor ?? base.palette.background.default;
  // White-label hosts commonly provide one brand colour. Never fall back to
  // ResponseDock's purple secondary colour inside an otherwise themed embed.
  const secondaryColor = theme.secondaryColor ?? primaryColor;
  const borderColor = theme.borderColor ?? base.palette.divider;
  const radius = numericRadius(theme.borderRadius);
  const buttonRadius = theme.buttonStyle === 'pill' ? 999 : theme.buttonStyle === 'square' ? 2 : radius;
  const paperShadow = theme.cardShadow === 'none'
    ? 'none'
    : theme.cardShadow === 'elevated'
      ? '0 12px 36px rgba(0,0,0,.16)'
      : '0 2px 10px rgba(0,0,0,.08)';
  const fontFamily = theme.fontFamily || base.typography.fontFamily;

  return createTheme(base, {
    palette: {
      mode,
      primary: { main: primaryColor, contrastText: theme.primaryTextColor ?? base.palette.primary.contrastText },
      secondary: { main: secondaryColor },
      background: { default: backgroundColor, paper: theme.surfaceColor ?? base.palette.background.paper },
      text: { primary: theme.textColor ?? base.palette.text.primary, secondary: theme.mutedColor ?? base.palette.text.secondary },
      divider: borderColor,
      error: { main: theme.errorColor ?? base.palette.error.main },
    },
    shape: { borderRadius: radius },
    typography: { fontFamily },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html, body, #root': { backgroundColor, backgroundImage: 'none' },
          body: { color: theme.textColor, fontFamily },
          ...(theme.fontUrl && theme.fontFamily ? {
            '@font-face': {
              fontFamily: theme.fontFamily,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              fontWeight: '100 900',
              src: `url(${JSON.stringify(theme.fontUrl)}) format("woff2")`,
            },
          } : {}),
        },
      },
      MuiButton: { styleOverrides: {
        root: { borderRadius: buttonRadius },
        sizeSmall: { borderRadius: buttonRadius },
        sizeLarge: { borderRadius: buttonRadius },
        containedPrimary: {
          '&:hover': { boxShadow: `0 4px 16px ${alpha(primaryColor, 0.2)}`, transform: 'translateY(-1px)' },
          '&:focus-visible': { boxShadow: `0 0 0 3px ${alpha(primaryColor, 0.2)}` },
        },
      } },
      MuiCard: { styleOverrides: { root: { borderColor, boxShadow: paperShadow } } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none', borderColor }, elevation1: { boxShadow: paperShadow } } },
      MuiDialog: { styleOverrides: { paper: { borderColor, boxShadow: paperShadow } } },
      MuiOutlinedInput: { styleOverrides: {
        root: {
          // Inputs sit on the host's own surface rather than ResponseDock's navy dark fill.
          ...(mode === 'dark' ? { backgroundColor: alpha(theme.textColor ?? '#FFFFFF', 0.04) } : {}),
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor, borderWidth: 1.5 },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor,
            borderWidth: 1.5,
            boxShadow: `0 0 0 3px ${alpha(primaryColor, 0.16)}`,
          },
        },
        notchedOutline: { borderColor },
      } },
      MuiInputLabel: { styleOverrides: { root: { '&.Mui-focused': { color: primaryColor } } } },
      MuiTabs: { styleOverrides: { indicator: { backgroundColor: primaryColor } } },
      MuiTab: { styleOverrides: { root: { '&.Mui-selected': { color: primaryColor } } } },
      MuiDivider: { styleOverrides: { root: { borderColor } } },
      MuiLink: { styleOverrides: { root: { color: primaryColor } } },
    },
  });
}

export const theme = createAppTheme('dark');
